<script setup lang="ts">
  import { useI18n } from 'vue-i18n'
  import WrapperQC from '@qicgroupdoha/qic-connect-ds/components/WrapperQC'
  import ButtonQC from '@qicgroupdoha/qic-connect-ds/components/ButtonQC'
  import BadgeQC from '@qicgroupdoha/qic-connect-ds/components/BadgeQC'
  import { setFormatDate, daysBetween, formatDate as _formatDate } from '@/shared/utils'
  import { useGoogleAnalytics } from '@/shared/composables/useGoogleAnalytics/useGoogleAnalytics'
  import { PolicyListItem } from '@/shared/types/policy.types'
  import { useLinkTarget } from '@/shared/composables/useLinkTarget'

  interface Props {
    item: PolicyListItem
  }

  type PolicyItemStatus = 'expired' | 'notActiveYet' | 'active'

  const props = defineProps<Props>()
  const { getLinkTarget } = useLinkTarget()
  const { locale, t } = useI18n()
  const gaService = useGoogleAnalytics()

  const getDateByString = (str: string): Date => {
    const [date, time] = str.split(' ')
    const [day, month, year] = date.split('/')
    const [hours, minutes] = time.split(':')
    return new Date(+year, +month - 1, +day, +hours, +minutes)
  }

  const formatDate = (date: Date): string => setFormatDate(date, locale.value as LocaleType)

  const startDate = getDateByString(props.item.policyStartDate)
  const endDate = getDateByString(props.item.policyEndDate)
  const currentDate = new Date()

  const getStatus = (): PolicyItemStatus => {
    if (endDate.getTime() < currentDate.getTime()) {
      return 'expired'
    }

    if (startDate.getTime() > currentDate.getTime()) {
      return 'notActiveYet'
    }

    return 'active'
  }

  const getStatusText = (status: PolicyItemStatus) => {
    if (status === 'active') {
      return t('qic_policies_list.policies_list.policy_status_active')
    }

    if (status === 'notActiveYet') {
      const daysDiff = daysBetween(startDate, currentDate)
      return t(
        'qic_policies_list.policies_list.policy_status_not_active',
        { days: daysDiff },
        daysDiff
      )
    }

    return `${t('qic_policies_list.policies_list.policy_status_expired')} ${formatDate(endDate)}`
  }

  const status = getStatus()
  const statusText = getStatusText(status)
  const dateDurationText = `${formatDate(startDate)} - ${formatDate(endDate)}`

  const getDownloadLink = `/policy/?action=getvisitorspdf&passportno=${props.item.passportNo}&policyno=${props.item.policyNo}`

  const onDownloadClick = () => {
    gaService.sendPolicyEvents(
      'click_download_policy',
      'available_policies',
      props.item.policyNo,
      _formatDate(startDate, 'en', 'dd.MM.yyyy'),
      _formatDate(endDate, 'en', 'dd.MM.yyyy')
    )
  }
</script>

<template>
  <WrapperQC class="list-card" direction="column" gap="var(--spacing-5)">
    <WrapperQC class="list-card__header">
      <WrapperQC direction="column" gap="var(--spacing-2)">
        <p class="list-card__title">
          {{ $t('qic_policies_list.policies_list.policy_number') }}
          <span class="list-card__policy ltr">#{{ item.policyNo }}</span>
        </p>

        <p class="list-card__date">
          {{ dateDurationText }}
        </p>
      </WrapperQC>

      <BadgeQC
        class="list-card__status"
        filled
        with-dot
        :variant="status === 'active' ? 'success' : 'neutral'"
      >
        {{ statusText }}
      </BadgeQC>
    </WrapperQC>

    <ButtonQC
      variant="secondary"
      size="medium"
      icon="file-download"
      data-test="download-button"
      wide
      :href="getDownloadLink"
      :target="getLinkTarget()"
      @click="onDownloadClick"
    >
      {{ $t('qic_policies_list.policies_list.download_button') }}
    </ButtonQC>
  </WrapperQC>
</template>

<style lang="scss" scoped>
  @use '@scss/utils.scss';
  @use '@qicgroupdoha/qic-connect-ds/scss/typography';

  .list-card {
    padding: var(--spacing-7);
    border-radius: var(--radius-6);
    border: 1px solid var(--colorBrdrSrfSecondary);
    background: var(--colorBgSrfPrimary);

    @include utils.mobile-flow {
      padding: var(--spacing-6);
    }
  }

  .list-card__header {
    justify-content: space-between;
    gap: var(--spacing-3);

    @include utils.mobile-flow {
      flex-direction: column;
      gap: var(--spacing-4);
    }
  }

  .list-card__title {
    @include typography.heading-5;

    @include utils.mobile-flow {
      @include typography.heading-6;
    }
  }

  .list-card__date {
    color: var(--colorTxtSrfTertiary);
    @include typography.text-regular;

    @include utils.mobile-flow {
      @include typography.text-small-regular;
    }
  }
</style>
